import React, { Fragment, useContext, useRef, useEffect } from "react";
import { Dialog, Transition } from "@headlessui/react";
import AppContext from "../../../context/AppContext";
import { FaPlusCircle } from "react-icons/fa";
import { toast } from "../../../utils/toast";
import CommonButton from "../../../components/common/CommonButton";
import ModalCloseButton from "../../../components/common/ModalCloseButton";
import { updatedMandatory } from "../../../service/workflow";
import translations from "../../../components/common/Translations";
import { isRTL } from "../../../utils/rtl";

export const MandatoryField = ({
    open,
    setOpen,
    manfields,
    setManFields,
    setTaskExcelColumns,
}) => {
    const cancelButtonRef = useRef(null);
    const { theme, langMode } = useContext(AppContext);
    const isRtl = isRTL(langMode);

    // Lock background page scroll while this modal is open, so only the modal's
    // own field-list scrolls -- avoids two visible scrollbars stacked together.
    useEffect(() => {
        if (!open) return;
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = previousOverflow;
        };
    }, [open]);

    useEffect(() => {
        if (open) {
            const savedFields = localStorage.getItem("selectedFields");
            if (savedFields) {
                const parsedFields = JSON.parse(savedFields);
                setManFields(parsedFields);
            } else {
                setManFields(prevFields => {
                    const updatedMandatory = (prevFields.mandatoryColumns || []).map(field => {
                        if (field.isMandatory && field.immutable) {
                            return { ...field, isReport: true };
                        }
                        return { ...field, isReport: field.isReport || false };
                    });
                    const updatedOther = (prevFields.otherColumns || []).map(field => {
                        return { ...field, isReport: field.isReport || false };
                    });
                    return {
                        mandatoryColumns: updatedMandatory,
                        otherColumns: updatedOther
                    };
                });
            }
        }
    }, [open, setManFields]);

    const handleCheckboxChange = (taskDetailsMetadataId) => {
        setManFields(prevFields => {
            const updatedMandatoryColumns = (prevFields.mandatoryColumns || []).map(field =>
                field.taskDetailsMetadataId === taskDetailsMetadataId && !(field.isMandatory && field.immutable)
                    ? { ...field, isMandatory: !field.isMandatory }
                    : field
            );
            const updatedOtherColumns = (prevFields.otherColumns || []).map(field =>
                field.taskDetailsMetadataId === taskDetailsMetadataId && !(field.isMandatory && field.immutable)
                    ? { ...field, isMandatory: !field.isMandatory }
                    : field
            );
            return {
                mandatoryColumns: updatedMandatoryColumns,
                otherColumns: updatedOtherColumns,
            };
        });
    };

    const handleUpdate = async () => {
        const allFields = [...(manfields?.mandatoryColumns || []), ...(manfields?.otherColumns || [])];
        const payload = allFields.map(field => ({
            taskDetailsMetadataId: field.taskDetailsMetadataId,
            isMandatory: field.isMandatory // isReport now determines if it's mandatory
        }));

        console.log("Payload to be sent:", payload);

        try {
            const data = await updatedMandatory(payload);
            if (data.header.code === 600) {
                toast.success(data.body.value)
            } else {
                toast.error(data.body.value)
            }
            setOpen(false);
        } catch (error) {
            toast.error(error?.response?.data?.body?.value || translations[langMode].failedtoupdatefield);
            console.error('Update failed:', error);
        }
    };

    const mandatoryFields = manfields?.mandatoryColumns || [];
    const otherFields = manfields?.otherColumns || [];

    return (
        <Transition.Root show={open} as={Fragment}>
            <Dialog
                as="div"
                className="relative z-50"
                initialFocus={cancelButtonRef}
                onClose={() => setOpen(false)}
            >
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div
                        className={`fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity`}
                    />
                </Transition.Child>
                <div className="fixed inset-0 z-50 w-screen overflow-y-auto">
                    <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                            <Dialog.Panel
                                dir={isRtl ? "rtl" : "ltr"}
                                className={`relative transform overflow-hidden rounded-lg bg-white text-left rtl:text-right shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-xl ${theme === "dark"
                                    ? "dark-mode"
                                    : theme === "high-contrast"
                                        ? "high-contrast"
                                        : ""
                                    }`}
                            >
                                <div
                                    className={`bg-white rounded-lg shadow ${theme === "dark"
                                        ? "dark-mode"
                                        : theme === "high-contrast"
                                            ? "high-contrast"
                                            : ""
                                        }`}
                                >
                                    <div className="flex items-center justify-between p-2 md:p-5 rounded-t dark:border-gray-600">
                                        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                                            {isRtl ? "حدد الحقول الإلزامية لسير العمل هذا" : "Select Mandatory Fields For This Workflow"}
                                        </h3>
                                        <ModalCloseButton
                                            onClick={() => setOpen(false)}
                                            ariaLabel="Close modal"
                                        />
                                    </div>
                                    <div className={`px-8 grid grid-cols-1 sm:grid-cols-2 gap-3 h-auto max-h-[300px] overflow-y-auto ${isRtl ? "text-right" : ""}`}>

                                        {mandatoryFields.map((field) => (
                                            <div
                                                key={field.taskDetailsMetadataId}
                                                className="flex flex-row items-center gap-2 mt-2"
                                            >
                                                <input
                                                    type="checkbox"
                                                    id={`field-${field.taskDetailsMetadataId}`}
                                                    checked={field.isMandatory}
                                                    onChange={() =>
                                                        handleCheckboxChange(field.taskDetailsMetadataId)
                                                    }
                                                    className="shrink-0"
                                                    disabled={field.immutable}
                                                />
                                                <label
                                                    htmlFor={`field-${field.taskDetailsMetadataId}`}
                                                    className="text-sm text-gray-900 dark:text-white"
                                                >
                                                    {field.fieldName}
                                                </label>
                                            </div>
                                        ))}

                                        {otherFields.map((field) => (
                                            <div
                                                key={field.taskDetailsMetadataId}
                                                className="flex items-center gap-2 mt-2"
                                            >
                                                <input
                                                    type="checkbox"
                                                    id={`field-${field.taskDetailsMetadataId}`}
                                                    checked={field.isMandatory}
                                                    onChange={() =>
                                                        handleCheckboxChange(field.taskDetailsMetadataId)
                                                    }
                                                    className="shrink-0"
                                                />
                                                <label
                                                    htmlFor={`field-${field.taskDetailsMetadataId}`}
                                                    className="text-sm text-gray-900 dark:text-white"
                                                >
                                                    {field.fieldName}
                                                </label>
                                            </div>
                                        ))}

                                    </div>
                                    <div className="p-5 flex justify-end rtl:justify-start">
                                        <CommonButton colorIntent="primary"
                                            variant="primary"
                                            onClick={handleUpdate}
                                            tooltipText={translations[langMode].updatefield}
                                            tooltipPosition="top"
                                        >
                                            {translations[langMode].updatefield}
                                        </CommonButton>
                                    </div>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>
    );
};
